import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Globe, Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCategories } from "@/hooks/useCategories";
import { useSiteContent, scValue, useCmsItems } from "@/hooks/useSiteContent";
import { categoryName } from "@/lib/helpers";
import { COMPANY } from "@/config/company";

const socialIcon = (value = "", network = "") => {
  const v = (network || value).toLowerCase();
  if (v.includes("facebook")) return Facebook;
  if (v.includes("instagram")) return Instagram;
  if (v.includes("linkedin")) return Linkedin;
  if (v.includes("youtube") || v.includes("youtu.be")) return Youtube;
  return Globe;
};

const Footer = () => {
  const { t, lang } = useI18n();
  const { categories } = useCategories();
  const sc = useSiteContent();
  const ft = sc?.footer;
  const footerItems = useCmsItems("footer_item");
  const year = new Date().getFullYear();
  const supportEmail = ft?.support_email || COMPANY.supportEmail;
  const tagline = scValue(ft, "tagline", lang, t("footer.tagline"));
  const rights = scValue(ft, "rights", lang, t("footer.rights"));
  const subtitle = scValue(sc?.hero, "subtitle", lang, t("hero.subtitle"));

  const socials = footerItems.filter((i) => i.data.type === "social" && i.data.value);
  const contacts = footerItems.filter((i) => ["link", "email", "phone", "whatsapp", "address", "text"].includes(i.data.type));
  const itemLabel = (d) => (lang === "en" ? d.label_en : d.label_it) || d.label_it || d.label_en || d.value || "";
  const itemHref = (d) => {
    if (d.type === "email") return `mailto:${d.value}`;
    if (d.type === "phone") return `tel:${d.value}`;
    if (d.type === "whatsapp") return `https://wa.me/${(d.value || "").replace(/[^0-9]/g, "")}`;
    if (d.type === "link" || d.type === "social") return d.value;
    return null;
  };
  const ContactIcon = (type) => ({ email: Mail, phone: Phone, whatsapp: MessageCircle, address: MapPin }[type] || Globe);

  return (
    <footer className="bg-brand-navy text-white/70 border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <img src="/logo-lsh-footer2.png" alt="LearnSphereHub" className="h-12 w-auto object-contain" />
          <p className="mt-4 max-w-sm text-sm leading-relaxed">{subtitle}</p>
          {socials.length > 0 && (
            <div className="mt-5 flex items-center gap-2" data-testid="footer-socials">
              {socials.map((s, i) => {
                const Icon = socialIcon(s.data.value, s.data.network);
                return (
                  <a key={s.id} data-testid={`footer-social-${i}`} href={s.data.value} target="_blank" rel="noopener noreferrer"
                    aria-label={itemLabel(s.data)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-brand-electric hover:border-brand-electric transition-colors">
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          )}
          {contacts.length > 0 && (
            <ul className="mt-5 space-y-2 text-sm" data-testid="footer-contacts">
              {contacts.map((c) => {
                const href = itemHref(c.data);
                const Icon = ContactIcon(c.data.type);
                const content = (<span className="inline-flex items-center gap-2"><Icon className="h-4 w-4 shrink-0 text-white/50" /> {itemLabel(c.data)}</span>);
                return (
                  <li key={c.id} data-testid={`footer-contact-${c.data.type}`}>
                    {href ? <a href={href} target={c.data.type === "link" ? "_blank" : undefined} rel="noopener noreferrer" className="hover:text-white transition-colors">{content}</a> : content}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t("footer.shop")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/prodotti" data-testid="footer-nav-products" className="hover:text-white transition-colors">{scValue(sc?.page_products, "title", lang, t("nav.products"))}</Link></li>
            {categories.map((c) => (
              <li key={c.slug}><Link to={`/categoria/${c.slug}`} data-testid={`footer-cat-${c.slug}`} className="hover:text-white transition-colors">{categoryName(c, lang)}</Link></li>
            ))}
            <li><Link to="/servizi-smart" data-testid="footer-nav-smart" className="hover:text-white transition-colors">{scValue(sc?.page_smart, "title", lang, t("nav.smart"))}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t("footer.about")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/chi-siamo" className="hover:text-white transition-colors">{t("nav.about")}</Link></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">{t("nav.faq")}</Link></li>
            <li><Link to="/contatti" className="hover:text-white transition-colors">{t("nav.contact")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t("footer.legal")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link data-testid="footer-privacy" to="/privacy" className="hover:text-white transition-colors">{t("legal.privacy")}</Link></li>
            <li><Link data-testid="footer-cookie" to="/cookie-policy" className="hover:text-white transition-colors">{t("legal.cookie")}</Link></li>
            <li><Link data-testid="footer-terms" to="/termini" className="hover:text-white transition-colors">{t("legal.terms")}</Link></li>
            <li><Link data-testid="footer-refund" to="/recesso-e-rimborsi" className="hover:text-white transition-colors">{t("legal.refund")}</Link></li>
            <li><a data-testid="footer-support" href={`mailto:${supportEmail}`} aria-label={`${t("footer.supportEmail")}: ${supportEmail}`} className="hover:text-white transition-colors whitespace-nowrap">{t("footer.supportEmail")}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 text-xs flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>© {year} {COMPANY.brand} — {COMPANY.seller}. {rights}</span>
          <span className="text-white/50">{tagline}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
